import { useEffect, useMemo, useState } from 'react';
import { demeterCalculatorsApi } from '../../../../Apis/Apis';
import applicationConstants from '../../../../Core/Utility/ApplicationConstants';
import useApi from '../../../Apis/Hooks/useApiHook';
import Dropdown from '../../../Components/Form/Inputs/Dropdown';
import TextInput from '../../../Components/Form/Inputs/TextInput';
import TutorialLink from '../../../Components/Form/Links/TutorialLink/TutorialLink';
import ComponentHeader from '../../../Components/Headers/ComponentHeader';
import TableSkeleton from '../../../Components/Layout/TableSkeleton';
import formattingService from '../../../Services/Formatting/FormattingService';
import useLanguage from '../../../Services/Language/useLanguageHook';
import styles from './FederalMilkMarketingOrderCalculator.module.scss';

export interface CalculationInputs {
    cheeseBlockPrice: string;
    cheeseBarrelPrice: string;
    dryWheyPrice: string;
    butterPrice: string;
    nonFatDryMilkPrice: string;
    cheeseBlockPercent: string;
}

interface CalculationResults {
    averageCheesePrice: number;
    milkClass3Price: number;
    milkClass4Price: number;
    milkClass1Price: number;
    milkClass2Price: number;
    butterfatPrice: number;
    proteinPrice: number;
    skimMilkClass3Price: number;
    skimMilkClass4Price: number;
    nonfatSolidsPrice: number;
    otherSolidsPrice: number;
}

const calculatorDatesAvailable = [new Date(2008, 9, 1), new Date(2025, 5, 1), new Date(2025, 11, 1)];

const FederalMilkMarketingOrderCalculator = () => {
    const [calculatorInputs, setCalculatorInputs] = useState<CalculationInputs>();
    const [calculationResults, setCalculationResults] = useState<CalculationResults>();
    const [calculatorDateSelection, setCalculatorDateSelection] = useState<Date>(new Date());

    const [translations] = useLanguage();
    const [loading, refreshApi, calculatorResponse] = useApi(() => demeterCalculatorsApi.getFederalMilkMarketingOrderCalculator());

    const calculatorDateOptions = useMemo(
        () =>
            calculatorDatesAvailable.map((x) => ({
                label: formattingService.toMonthYear(x),
                value: x,
            })),
        [],
    );

    useEffect(() => {
        if (!calculatorDateOptions) {
            return;
        }

        setCalculatorDateSelection(calculatorDateOptions[2].value);
    }, [calculatorDateOptions]);

    useEffect(() => {
        if (!calculatorResponse) {
            return;
        }
        setCalculatorInputs({
            cheeseBlockPrice: calculatorResponse.cheeseBlockPrice!.toString(),
            cheeseBarrelPrice: calculatorResponse.cheeseBarrelPrice!.toString(),
            dryWheyPrice: calculatorResponse.dryWheyPrice!.toString(),
            butterPrice: calculatorResponse.butterPrice!.toString(),
            nonFatDryMilkPrice: calculatorResponse.nonFatDryMilkPrice!.toString(),
            cheeseBlockPercent: calculatorResponse.cheeseBlockPercent!.toString(),
        });
    }, [calculatorResponse]);

    useEffect(() => {
        if (calculatorInputs?.cheeseBlockPercent) {
            let proteinPriceVariable1 = 1.572;
            let proteinPriceVariable2 = 0.9;
            let class3PriceVariable1 = 3.1;
            let class3PriceVariable2 = 5.9;
            let class4AndClass2PriceVariable1 = 9;

            let averageCheesePrice = +(
                +calculatorInputs!.cheeseBlockPrice! * (+calculatorInputs!.cheeseBlockPercent! / 100) +
                (+calculatorInputs!.cheeseBarrelPrice! + 0.03) * (getCheeseBarrelPercent() / 100)
            ).toFixed(4);

            let pricingVariable1 = 0.2003;
            let pricingVariable2 = 0.1991;
            let pricingVariable3 = 0.1715;
            let pricingVariable4 = 0.1678;

            const isDecember = calculatorDateSelection.getTime() === calculatorDateOptions[2].value.getTime();
            const isJune = calculatorDateSelection.getTime() >= calculatorDateOptions[1].value.getTime();

            if (isJune || isDecember) {
                averageCheesePrice = +calculatorInputs!.cheeseBlockPrice!;

                pricingVariable1 = 0.2519;
                pricingVariable2 = 0.2668;
                pricingVariable3 = 0.2272;
                pricingVariable4 = 0.2393;
            }

            if (isJune) {
                proteinPriceVariable1 = 1.589;
                proteinPriceVariable2 = 0.91;
            }

            if (isDecember) {
                class3PriceVariable1 = 3.3;
                class3PriceVariable2 = 6;
                class4AndClass2PriceVariable1 = 9.3;
            }

            const butterfatPrice = +((+calculatorInputs!.butterPrice - pricingVariable3) * 1.211).toFixed(4);
            const proteinPrice = +(
                (averageCheesePrice - pricingVariable1) * 1.383 +
                ((averageCheesePrice - pricingVariable1) * proteinPriceVariable1 - butterfatPrice * proteinPriceVariable2) * 1.17
            ).toFixed(4);
            const otherSolidsPrice = +((+calculatorInputs!.dryWheyPrice! - pricingVariable2) * 1.03).toFixed(4);
            const skimMilkClass3Price = +(proteinPrice * class3PriceVariable1 + otherSolidsPrice * class3PriceVariable2).toFixed(4);
            const milkClass3Price = +(skimMilkClass3Price * 0.965 + butterfatPrice * 3.5).toFixed(2);
            const nonfatSolidsPrice = +((+calculatorInputs!.nonFatDryMilkPrice! - pricingVariable4) * 0.99).toFixed(4);
            const skimMilkClass4Price = +(nonfatSolidsPrice * class4AndClass2PriceVariable1).toFixed(4);
            const milkClass4Price = +(skimMilkClass4Price * 0.965 + butterfatPrice * 3.5).toFixed(2);
            const skimMilkClass3AndClass4AveragePrice = +((skimMilkClass3Price + skimMilkClass4Price) / 2.0).toFixed(4);
            const maximumOrAverageSkimMilk =
                isJune || isDecember ? Math.max(skimMilkClass3Price, skimMilkClass4Price) : skimMilkClass3AndClass4AveragePrice + 0.74;
            const milkClass1Price = +(0.965 * maximumOrAverageSkimMilk + 3.5 * butterfatPrice).toFixed(2);
            const milkClass2Price = +(
                +(0.965 * +(nonfatSolidsPrice * class4AndClass2PriceVariable1 + 0.7).toFixed(2)).toFixed(2) +
                +(butterfatPrice + 0.007).toFixed(4) * 3.5
            ).toFixed(2);

            setCalculationResults({
                averageCheesePrice,
                butterfatPrice,
                proteinPrice,
                skimMilkClass3Price,
                skimMilkClass4Price,
                nonfatSolidsPrice,
                otherSolidsPrice,
                milkClass3Price,
                milkClass4Price,
                milkClass1Price,
                milkClass2Price,
            });
        }
    }, [calculatorInputs, calculatorDateSelection]);

    const getCheeseBarrelPercent = (): number => 100 - +(calculatorInputs?.cheeseBlockPercent ?? 50);
    const usingCurrentYearDataModifiers = calculatorDateSelection?.getTime() === calculatorDateOptions[0].value.getTime();

    const isInitialLoading = loading && !calculatorInputs;

    return (
        <div>
            <div className={styles.calculators_sub_heading_main}>
                <div className={styles.calculators_sub_heading_section}>
                    <ComponentHeader
                        containerClassName={styles.calculators_sub_heading_bold_item}
                        title={translations.calculators.text.federalMilkMarketingOrder}
                    />
                    <Dropdown
                        value={calculatorDateSelection}
                        options={calculatorDateOptions}
                        handleOptionChange={(value) => setCalculatorDateSelection(value)}
                        track={{ eventCategory: 'FederalMilkMarketingOrderCalculator', eventAction: 'DateChanged' }}
                    />
                    <div className={styles.calculators_tutorial_container}>
                        <TutorialLink link={applicationConstants.TutorialLinks.federalMilkCalculator} />
                    </div>
                </div>
                <div className={styles.calculators_sub_heading_item}>{translations.calculators.text.federalMilkMarketingOrderDescription}</div>
            </div>
            {isInitialLoading ? (
                <TableSkeleton />
            ) : (
                <>
                    <div data-testid="FederalMilkMarketingOrderCalculator" className={styles.calculators_calculation_container}>
                        <div className={styles.calculators_calculation_heading}>{translations.calculators.text.milkPriceCalculator}</div>
                        <div className={styles.calculators_input_container}>
                            <div className={styles.calculators_input}>
                                <TextInput
                                    title={`${translations.commodity.CheeseBlock} ${translations.words.price}`}
                                    type="number"
                                    required
                                    value={calculatorInputs?.cheeseBlockPrice?.toString()}
                                    handleTextChange={(value) => setCalculatorInputs({ ...calculatorInputs!, cheeseBlockPrice: value ?? '' })}
                                    track={{ eventCategory: 'FederalMilkMarketingOrderCalculator', eventAction: 'CheeseBlockPriceChanged' }}
                                />
                            </div>
                            {usingCurrentYearDataModifiers && (
                                <div className={styles.calculators_input}>
                                    <TextInput
                                        title={`${translations.commodity.CheeseBarrel} ${translations.words.price}`}
                                        type="number"
                                        required
                                        value={calculatorInputs?.cheeseBarrelPrice?.toString()}
                                        handleTextChange={(value) => setCalculatorInputs({ ...calculatorInputs!, cheeseBarrelPrice: value ?? '' })}
                                        track={{ eventCategory: 'FederalMilkMarketingOrderCalculator', eventAction: 'CheeseBarrelPriceChanged' }}
                                    />
                                </div>
                            )}
                            <div className={styles.calculators_input}>
                                <TextInput
                                    title={`${translations.commodity.DryWhey} ${translations.words.price}`}
                                    type="number"
                                    required
                                    value={calculatorInputs?.dryWheyPrice?.toString()}
                                    handleTextChange={(value) => setCalculatorInputs({ ...calculatorInputs!, dryWheyPrice: value ?? '' })}
                                    track={{ eventCategory: 'FederalMilkMarketingOrderCalculator', eventAction: 'DryWheyPriceChanged' }}
                                />
                            </div>
                            <div className={styles.calculators_input}>
                                <TextInput
                                    title={`${translations.commodity.Butter} ${translations.words.price}`}
                                    type="number"
                                    required
                                    value={calculatorInputs?.butterPrice?.toString()}
                                    handleTextChange={(value) => setCalculatorInputs({ ...calculatorInputs!, butterPrice: value ?? '' })}
                                    track={{ eventCategory: 'FederalMilkMarketingOrderCalculator', eventAction: 'ButterPriceChanged' }}
                                />
                            </div>
                            <div className={styles.calculators_input}>
                                <TextInput
                                    title={`${translations.commodity.NonFatDryMilk} ${translations.words.price}`}
                                    type="number"
                                    required
                                    value={calculatorInputs?.nonFatDryMilkPrice?.toString()}
                                    handleTextChange={(value) => setCalculatorInputs({ ...calculatorInputs!, nonFatDryMilkPrice: value ?? '' })}
                                    track={{ eventCategory: 'FederalMilkMarketingOrderCalculator', eventAction: 'NonFatDryMilkPriceChanged' }}
                                />
                            </div>
                        </div>
                        {usingCurrentYearDataModifiers && (
                            <>
                                <div className={styles.calculators_calculation_heading}>{translations.calculators.text.cheesePricing}</div>
                                <div className={styles.calculators_input_container}>
                                    <div className={styles.calculators_input}>
                                        <TextInput
                                            title={`${translations.commodity.CheeseBlock} %`}
                                            type="number"
                                            required
                                            value={calculatorInputs?.cheeseBlockPercent?.toString()}
                                            handleTextChange={(value) => setCalculatorInputs({ ...calculatorInputs!, cheeseBlockPercent: value ?? '' })}
                                            track={{ eventCategory: 'FederalMilkMarketingOrderCalculator', eventAction: 'CheeseBlockPercentChanged' }}
                                        />
                                        <div className={styles.calculators_percent_label}> % </div>
                                    </div>
                                    <div className={styles.calculators_input}>
                                        <TextInput
                                            title={`${translations.commodity.CheeseBarrel} %`}
                                            type="number"
                                            required
                                            value={getCheeseBarrelPercent().toString()}
                                            handleTextChange={(value) => setCalculatorInputs({ ...calculatorInputs!, cheeseBlockPercent: `${100 - +value}` })}
                                            track={{ eventCategory: 'FederalMilkMarketingOrderCalculator', eventAction: 'CheeseBarrelPercentChanged' }}
                                        />
                                        <div className={styles.calculators_percent_label}> % </div>
                                    </div>

                                    <div className={styles.calculators_average_price}>
                                        <div className={styles.calculators_result_price_heading}>{translations.calculators.text.averageCheesePrice}</div>
                                        <div className={styles.calculators_average_price_unit}>
                                            ${calculationResults?.averageCheesePrice} / {translations.unitOfMeasure.ShortPoundSingle}
                                        </div>
                                    </div>
                                </div>
                            </>
                        )}
                        <button className={styles.calculators_button_cancel} type="button" onClick={() => refreshApi()}>
                            {translations.actions.reset}
                        </button>
                    </div>
                    <div data-testid="FederalMilkMarketingOrderCalculatorResult" className={styles.calculators_result_container}>
                        <div className={styles.calculators_calculation_heading}>{translations.calculators.text.result}</div>
                        <div className={styles.calculators_result_upper_container}>
                            <div className={styles.calculators_result_upper_item}>
                                <div className={styles.calculators_result_price}>
                                    <div className={styles.calculators_result_price_heading}>{translations.commodity.MilkClass3}</div>
                                    <div className={styles.calculators_result_price_unit}>
                                        ${formattingService.formatPriceNumber(calculationResults?.milkClass3Price)} /{' '}
                                        {translations.unitOfMeasure.ShortHundredWeight}
                                    </div>
                                </div>
                                <div className={styles.calculators_result_price}>
                                    <div className={styles.calculators_result_price_heading}>{translations.commodity.MilkClass1}</div>
                                    <div className={styles.calculators_result_price_unit}>
                                        ${formattingService.formatPriceNumber(calculationResults?.milkClass1Price)} /{' '}
                                        {translations.unitOfMeasure.ShortHundredWeight}
                                    </div>
                                </div>
                            </div>
                            <div className={styles.calculators_result_upper_item}>
                                <div className={styles.calculators_result_price}>
                                    <div className={styles.calculators_result_price_heading}>{translations.commodity.MilkClass4}</div>

                                    <div className={styles.calculators_result_price_unit}>
                                        ${formattingService.formatPriceNumber(calculationResults?.milkClass4Price)} /{' '}
                                        {translations.unitOfMeasure.ShortHundredWeight}
                                    </div>
                                </div>
                                <div className={styles.calculators_result_price}>
                                    <div className={styles.calculators_result_price_heading}>{translations.commodity.MilkClass2}</div>
                                    <div className={styles.calculators_result_price_unit}>
                                        ${formattingService.formatPriceNumber(calculationResults?.milkClass2Price)} /{' '}
                                        {translations.unitOfMeasure.ShortHundredWeight}
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className={styles.calculators_result_table}>
                            <div className={styles.calculators_result_table_row}>
                                <div>{translations.words.product}</div>
                                <div>{translations.words.price}</div>
                            </div>
                            <div className={styles.calculators_result_table_bar} />
                            <div className={styles.calculators_result_table_row}>
                                <div>
                                    {translations.commodity.Butterfat} {translations.words.price}
                                </div>
                                <div>
                                    {formattingService.formatPriceNumber(calculationResults?.butterfatPrice)} / {translations.unitOfMeasure.ShortPoundSingle}
                                </div>
                            </div>
                            <div className={styles.calculators_result_table_bar} />
                            <div className={styles.calculators_result_table_row}>
                                <div>
                                    {translations.commodity.Protein} {translations.words.price}
                                </div>
                                <div>
                                    {formattingService.formatPriceNumber(calculationResults?.proteinPrice)} / {translations.unitOfMeasure.ShortPoundSingle}
                                </div>
                            </div>
                            <div className={styles.calculators_result_table_bar} />
                            <div className={styles.calculators_result_table_row}>
                                <div>
                                    {translations.commodity.SkimMilkClass3} {translations.words.price}
                                </div>
                                <div>
                                    {formattingService.formatPriceNumber(calculationResults?.skimMilkClass3Price)} /{' '}
                                    {translations.unitOfMeasure.ShortHundredWeight}
                                </div>
                            </div>
                            <div className={styles.calculators_result_table_bar} />
                            <div className={styles.calculators_result_table_row}>
                                <div>
                                    {translations.commodity.SkimMilkClass4} {translations.words.price}
                                </div>
                                <div>
                                    {formattingService.formatPriceNumber(calculationResults?.skimMilkClass4Price)} /{' '}
                                    {translations.unitOfMeasure.ShortHundredWeight}
                                </div>
                            </div>
                            <div className={styles.calculators_result_table_bar} />
                            <div className={styles.calculators_result_table_row}>
                                <div>
                                    {translations.commodity.NonFatSolids} {translations.words.price}
                                </div>
                                <div>
                                    {formattingService.formatPriceNumber(calculationResults?.nonfatSolidsPrice)} / {translations.unitOfMeasure.ShortPoundSingle}
                                </div>
                            </div>
                            <div className={styles.calculators_result_table_bar} />
                            <div className={styles.calculators_result_table_row}>
                                <div>
                                    {translations.commodity.OtherSolids} {translations.words.price}
                                </div>
                                <div>
                                    {formattingService.formatPriceNumber(calculationResults?.otherSolidsPrice)} / {translations.unitOfMeasure.ShortPoundSingle}
                                </div>
                            </div>
                            <div className={styles.calculators_result_table_bar} />
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};

export default FederalMilkMarketingOrderCalculator;
